import { useEffect } from 'react';
import { Alert } from '@00start/ui';
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { DashboardProvider, useDashboard } from '@/hooks/useDashboard';
import { AuthGate } from '@/components/AuthGate';
import { Sidebar } from '@/components/Sidebar';
import { MobileTabBar } from '@/components/MobileTabBar';
import { MobileStatusBar } from '@/components/MobileStatusBar';
import { HealthView } from '@/views/HealthView';
import { IncidentsView } from '@/views/IncidentsView';
import { useOverview } from '@/hooks/useOverview';
import { useIncidents } from '@/hooks/useIncidents';

function Dashboard() {
  const { view, setView, isOnline, hasCriticalIncidents, setCriticalFlag } = useDashboard();
  const { data: overview } = useOverview();
  const { incidents } = useIncidents('open');

  const services = overview?.health?.orgs?.[0]?.services || [];
  const healthyCount = services.filter((s) => ['healthy', 'up', 'ok'].includes(s.status?.toLowerCase())).length;

  useEffect(() => {
    setCriticalFlag(incidents);
  }, [incidents, setCriticalFlag]);

  useEffect(() => {
    if (hasCriticalIncidents && window.innerWidth < 768) {
      setView('incidents');
    }
  }, [hasCriticalIncidents, setView]);

  return (
    <>
      {!isOnline && (
        <Alert variant="warning" className="m-4">
          Offline — showing cached data. Actions disabled.
        </Alert>
      )}
      <MobileStatusBar serviceCount={services.length} healthyCount={healthyCount} incidentCount={incidents.length} />
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 pb-16 md:pb-0">
          {view === 'health' ? <HealthView /> : <IncidentsView />}
        </main>
      </div>
      <MobileTabBar />
    </>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <AuthGate />;
  return (
    <DashboardProvider>
      <Dashboard />
    </DashboardProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}
